import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import {
  fetchEmailWidgets, createEmailWidget, updateEmailWidget,
  setDefaultEmailWidget, toggleEmailWidgetStatus, deleteEmailWidget,
} from "../services/emailWidgetService";

/** Email widgets (2026-10-27) — company email designs. */

const KEY = "email-widgets";

export const useEmailWidgets = () =>
  useQuery({ queryKey: [KEY], queryFn: fetchEmailWidgets, staleTime: 60 * 1000 });

/** Only the widgets that can be used right now — for pickers (Bulk Email, templates, inboxes). */
export const useActiveEmailWidgets = () => {
  const query = useEmailWidgets();
  const active = (query.data || []).filter((w) => Number(w.status) === 1);
  return { ...query, data: active };
};

const useWidgetMutation = (mutationFn, successMessage) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: () => {
      if (successMessage) toast.success(successMessage);
      queryClient.invalidateQueries({ queryKey: [KEY], refetchType: "all" });
    },
    // The service layer has already turned 403 / 404 / 409 / 422 into a readable line.
    onError: (err) => toast.error(err?.message || "Something went wrong"),
  });
};

export const useCreateEmailWidget = () =>
  useWidgetMutation((payload) => createEmailWidget(payload), "Widget saved");

export const useUpdateEmailWidget = () =>
  useWidgetMutation(({ id, ...payload }) => updateEmailWidget(id, payload), "Widget saved");

export const useSetDefaultEmailWidget = () =>
  useWidgetMutation((id) => setDefaultEmailWidget(id), "Default design updated");

export const useToggleEmailWidget = () =>
  useWidgetMutation((id) => toggleEmailWidgetStatus(id), null);

export const useDeleteEmailWidget = () =>
  useWidgetMutation((id) => deleteEmailWidget(id), "Widget deleted");
